'use client';

import { useTranslations } from 'next-intl';
import { useWishlist, useCustomer } from '@cobrastyle/storefront-core/hooks';
import { StoreLink } from '@/components/store';
import { WishlistBookmarkIcon } from '@/components/ui/wishlist-mark';

/**
 * The header's "Favoriter" link, carrying how many are saved.
 *
 * Torsbo asked for a visible marker so it is obvious something landed there
 * (p.4). Deliberately the same treatment as the "Jämför" link beside it — see
 * components/compare/compare-link.tsx — rather than the cart's red bubble: both
 * of these are saved lists, and the bubble is the cart's own signal that money
 * is pending.
 *
 * The count only appears once something is saved. "Favoriter (0)" reads as a
 * broken link, and before anything is saved the link is an invitation.
 *
 * `useWishlist` returns nothing at all for a signed-out visitor, so this shows
 * no count then — which is correct: there is no list yet.
 *
 * A confirmed guest is sent to sign-in rather than to the favourites page,
 * carrying the reason so the form can say why (p.5). The account layout would
 * redirect them anyway, but it guards orders and profile too, so a
 * favourites-specific message cannot live there. While customer state is still
 * loading the normal link is kept: sending a signed-in customer to a login
 * form would be worse than a moment of the plain destination.
 */
interface WishlistLinkProps {
  className?: string;
  /**
   * `text` is the desktop row's word-and-count, matching "Jämför" beside it.
   * `icon` is the mobile row, where the word does not fit next to the cart —
   * there the count rides as a bubble on the mark, like CartButton's.
   */
  variant?: 'text' | 'icon';
}

export function WishlistLink({ className, variant = 'text' }: WishlistLinkProps) {
  const t = useTranslations('nav');
  const { wishlist } = useWishlist();
  const { customer, isLoading: isCustomerLoading } = useCustomer();
  const count = wishlist?.itemsCount ?? 0;
  const shown = count > 99 ? '99+' : count;
  const href =
    !customer && !isCustomerLoading
      ? '/konto/logga-in?reason=wishlist'
      : '/konto/onskelista';

  if (variant === 'icon') {
    return (
      <StoreLink
        href={href}
        className={className}
        aria-label={count > 0 ? `${t('favorites')} · ${shown}` : t('favorites')}
      >
        {/* The same mark the product cards save with, at the same 20px — a
            heart here and a bookmark on the card read as two features, not one
            list. Never filled: this is the way in, not a saved state. */}
        <WishlistBookmarkIcon className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-600 px-1 text-[10px] font-semibold leading-none text-white">
            {shown}
          </span>
        )}
      </StoreLink>
    );
  }

  return (
    <StoreLink href={href} className={className}>
      {t('favorites')}
      {count > 0 && ` (${shown})`}
    </StoreLink>
  );
}
